// ===================== Uniform spatial hash grid =====================
// Rebuilt each frame from the enemy list; used to answer "what enemies are
// near (x,y) within r" cheaply for projectile and pickup collision.
export class SpatialHash {
  constructor(cellSize = 80) {
    this.cell = cellSize;
    this.map = new Map(); // key "cx,cy" -> array of entities
    this._queryBuf = [];
  }
  _key(cx, cy) { return cx * 73856093 ^ cy * 19349663; }

  clear() { this.map.clear(); }

  insert(ent) {
    const cx = Math.floor(ent.x / this.cell);
    const cy = Math.floor(ent.y / this.cell);
    const k = this._key(cx, cy);
    let bucket = this.map.get(k);
    if (!bucket) { bucket = []; this.map.set(k, bucket); }
    bucket.push(ent);
  }

  rebuild(entities) {
    this.clear();
    for (const e of entities) this.insert(e);
  }

  // Returns entities in cells overlapping the circle (x,y,r). May include a
  // few extras just outside r — callers do a precise distance check.
  //
  // Returns the shared `_queryBuf` by default, which the NEXT query clobbers.
  // A caller that can trigger a nested query while iterating (e.g. a collision
  // loop whose hit handler runs element effects that re-query) must pass its
  // own persistent `out` array instead of allocating one per call.
  query(x, y, r, out = this._queryBuf) {
    const buf = out;
    buf.length = 0;
    const c = this.cell;
    const minx = Math.floor((x - r) / c);
    const maxx = Math.floor((x + r) / c);
    const miny = Math.floor((y - r) / c);
    const maxy = Math.floor((y + r) / c);
    for (let cx = minx; cx <= maxx; cx++) {
      for (let cy = miny; cy <= maxy; cy++) {
        const bucket = this.map.get(this._key(cx, cy));
        if (bucket) {
          for (let i = 0; i < bucket.length; i++) buf.push(bucket[i]);
        }
      }
    }
    return buf;
  }

  // Nearest live entity to (x,y) within maxR (searches expanding rings).
  nearest(x, y, maxR, filter) {
    const list = this.query(x, y, maxR);
    let best = null, bestD = maxR * maxR;
    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      if (e.dead) continue;
      if (filter && !filter(e)) continue;
      const dx = e.x - x, dy = e.y - y;
      const d = dx * dx + dy * dy;
      if (d < bestD) { bestD = d; best = e; }
    }
    return best;
  }
}
